import { useEffect, useRef, useState } from "react";
import {
  DatePicker,
  Input,
  InputNumber,
  Checkbox,
  Table,
  Card,
  Statistic,
  Row,
  Col,
  Divider,
  Switch,
  Button,
  Tooltip,
  Modal,
  Select,
  Tag,
  message,
} from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import {
  InfoCircleOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import ableToModifyPurchaseRate from "../../../../../../src/utils/ableToModifyPurchaseRate";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const CHARGE_TYPE_LABELS = {
  AGENCY_COMMISSION: "Agency Commission",
  TRANSPORTATION: "Transportation",
};

const InventoryList = ({
  salesOrderData,
  handleInvChange,
  setSalesOrderData,
  handleFormChange,
  log,
  isSubBdf = false,
  updateOwnedByOfTradingInv = async () => false,
}) => {
  const [dataSource, setDataSource] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bInv, setBInv] = useState([]);
  const [hidePurchaseRate, setHidePurchaseRate] = useState(false);
  const [hideprintingMounting, setHidePrintingMounting] = useState(true);
  const [ownedByModalOpen, setOwnedByModalOpen] = useState(false);
  const [ownedByInvId, setOwnedByInvId] = useState(null);
  const [ownedByValue, setOwnedByValue] = useState("Other Trading");
  const [ownedBySaving, setOwnedBySaving] = useState(false);

  // Agency Commission / Transportation charge line modal
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [chargeType, setChargeType] = useState("AGENCY_COMMISSION");
  const [chargeAmount, setChargeAmount] = useState(null);
  const [chargeDates, setChargeDates] = useState([]);
  const [chargePartyId, setChargePartyId] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partySearching, setPartySearching] = useState(false);
  const partySearchTimer = useRef(null);

  let userId = useSelector((state) => state.user._id);
  const [totals, setTotals] = useState({
    purchaseRate: 0,
    billingAmount: 0,
    specialRatePerMonth: 0,
    profitLoss: 0,
  });

  console.log("Sales order inv data: ", salesOrderData);
  // console.log("Sales order inv log: ", log);

  useEffect(() => {
    if (salesOrderData?.SOConfirmed) {
      let inv;
      if (salesOrderData?.billingInv?.length > 0) {
        inv = salesOrderData?.billingInv?.map((ele) => ({
          ...ele,
          billingStartDate: ele.billingStartDate,
          billingEndDate: ele.billingEndDate,
        }));
      } else {
        inv = salesOrderData.inventories.map((ele) => ({
          ...ele,
          billingStartDate: ele.startDate,
          billingEndDate: ele.endDate,
        }));
        setBInv([...inv]);
        setDataSource([...inv]);
      }

      const allIds = inv.map((item) => item._id);
      setSelectedRows(allIds);
    } else {
      setBInv([...(salesOrderData?.inventories || [])]);
      setDataSource([...(salesOrderData?.inventories || [])]);

      const allIds = (salesOrderData?.inventories || []).map(
        (item) => item._id,
      );
      setSelectedRows(allIds);
    }
  }, [salesOrderData]);

  useEffect(() => {
    setDataSource([...bInv]);
    calculateTotals([...bInv]);
  }, [bInv]);

  const getPreviousInventory = (recordId) => {
    return log?.inventories?.find((inv) => inv._id === recordId);
  };

  const hasValueChanged = (currentValue, previousValue) => {
    return Number(currentValue || 0) !== Number(previousValue || 0);
  };

  const calculateTotals = (inventories) => {
    let totalPurchaseRate = 0;
    let totalBillingAmount = 0;
    let totalSpecialRatePerMonth = 0;
    let totalProfitLoss = 0;
    let validItemsCount = 0;

    inventories.forEach((record) => {
      // Calculate purchase rate (direct maintenanceRate)
      const purchaseRate = Number(record.maintenanceRate) || 0;

      // Calculate billing amount (special rate)
      const billingAmount = Number(record.specialRate) || 0;

      // Get special rate per month
      const specialRatePerMonth = Number(record.specialRatePerMonth) || 0;

      // Calculate profit/loss
      let profitLoss = 0;
      if (purchaseRate && billingAmount) {
        profitLoss = ((billingAmount - purchaseRate) / billingAmount) * 100;
        validItemsCount++;
      }

      totalPurchaseRate += purchaseRate;
      totalBillingAmount += billingAmount;
      totalSpecialRatePerMonth += specialRatePerMonth;
      totalProfitLoss += profitLoss;
    });

    // Calculate average profit/loss
    const avgProfitLoss =
      validItemsCount > 0 ? totalProfitLoss / validItemsCount : 0;

    setTotals({
      purchaseRate: totalPurchaseRate,
      billingAmount: totalBillingAmount,
      specialRatePerMonth: totalSpecialRatePerMonth,
      profitLoss: avgProfitLoss,
    });
  };

  const handleCheckboxChange = (checked, recordId) => {
    setSelectedRows((prevSelectedRows) => {
      const updatedSelectedRows = checked
        ? [...prevSelectedRows, recordId]
        : prevSelectedRows.filter((id) => id !== recordId);

      let inv = bInv.filter((ele) => {
        return updatedSelectedRows.includes(ele._id);
      });

      setSalesOrderData((prev) => {
        return {
          ...prev,
          billingInv: inv,
        };
      });
      return updatedSelectedRows;
    });

    setBInv((prevBInv) =>
      prevBInv.map((item) =>
        item._id === recordId ? { ...item, isSelected: checked } : item,
      ),
    );
  };

  const calculateCampaignDuration = (inventories) => {
    const dates = inventories.map((ele) => ({
      startDate: dayjs(ele.startDate),
      endDate: dayjs(ele.endDate),
    }));
    const minStartDate = dayjs.min(dates.map((d) => d.startDate));
    const maxEndDate = dayjs.max(dates.map((d) => d.endDate));
    const differenceInDays = maxEndDate.diff(minStartDate, "day") + 1;
    handleFormChange("campaignDuration", differenceInDays);
  };

  const handleDateRangeChange = (dates, dateStrings, recordId) => {
    if (dateStrings && dateStrings.length === 2) {
      const inventories = salesOrderData?.inventories.map((ele) =>
        ele._id === recordId
          ? { ...ele, startDate: dateStrings[0], endDate: dateStrings[1] }
          : ele,
      );
      setSalesOrderData((prev) => ({ ...prev, inventories }));
      calculateCampaignDuration(inventories);
    }
  };

  const handleBillingDateRangeChange = (dates, dateStrings, recordId) => {
    if (dateStrings && dateStrings.length === 2) {
      const updatedBInv = bInv.map((ele) =>
        ele._id === recordId
          ? {
              ...ele,
              billingStartDate: dateStrings[0],
              billingEndDate: dateStrings[1],
            }
          : ele,
      );
      setBInv(updatedBInv);
      let inv = updatedBInv.filter((ele) => {
        return selectedRows.includes(ele._id);
      });

      setSalesOrderData((prev) => {
        return {
          ...prev,
          billingInv: inv,
        };
      });
    }
  };

  const openOwnedByModal = (record) => {
    setOwnedByInvId(record?._id || null);
    setOwnedByValue("Other Trading");
    setOwnedByModalOpen(true);
  };

  const handleOwnedBySave = async () => {
    if (!ownedByInvId) return;
    setOwnedBySaving(true);
    try {
      const ok = await updateOwnedByOfTradingInv(ownedByInvId, ownedByValue);
      if (ok) {
        setOwnedByModalOpen(false);
      }
    } finally {
      setOwnedBySaving(false);
    }
  };

  const openChargeModal = () => {
    setChargeType("AGENCY_COMMISSION");
    setChargeAmount(null);
    setChargeDates([]);
    setChargePartyId(null);
    setSelectedParty(null);
    setPartyOptions([]);
    setChargeModalOpen(true);
  };

  const partyOptionLabel = (party) => {
    const name = party?.name || "Unnamed Party";
    const gst = party?.gstNumber ? `GST: ${party.gstNumber}` : "No GST";
    return `${name} — ${gst}`;
  };

  const searchParties = (search) => {
    if (partySearchTimer.current) clearTimeout(partySearchTimer.current);
    partySearchTimer.current = setTimeout(async () => {
      setPartySearching(true);
      try {
        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/listPartyCompanies?search=${encodeURIComponent(
            search || "",
          )}&limit=20`,
          { method: "GET", credentials: "include" },
        );
        const result = await response.json();
        if (result?.success) {
          // Keep the currently selected party visible even if it falls out of
          // the new search results, so the Select doesn't lose its label.
          const fetched = result.data || [];
          const merged = selectedParty?._id
            && !fetched.some((p) => p._id === selectedParty._id)
            ? [selectedParty, ...fetched]
            : fetched;
          setPartyOptions(merged);
        }
      } catch (err) {
        console.error("Error searching parties:", err);
      } finally {
        setPartySearching(false);
      }
    }, 400);
  };

  const handlePartySelect = (id) => {
    const party = partyOptions.find((p) => p._id === id) || null;
    setChargePartyId(id);
    setSelectedParty(party);
  };

  const handleAddCharge = () => {
    if (!chargeAmount || Number(chargeAmount) <= 0) {
      message.error("Please enter a valid amount");
      return;
    }
    if (!chargeDates?.[0] || !chargeDates?.[1]) {
      message.error("Please select a duration");
      return;
    }
    if (!selectedParty?.name) {
      message.error("Please select a party");
      return;
    }

    const newRow = {
      _id: `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      lineType: chargeType,
      commissionForId: selectedParty._id,
      ownedBy: selectedParty.name,
      ownedByGst: selectedParty.gstNumber || null,
      maintenanceRate: Number(chargeAmount),
      specialRate: 0,
      specialRatePerMonth: 0,
      printingCharge: 0,
      mountingCharge: 0,
      innovationRate: 0,
      startDate: chargeDates[0].format("YYYY-MM-DD"),
      endDate: chargeDates[1].format("YYYY-MM-DD"),
      CodeNo: null,
      locationName: null,
      suffix: null,
      width: null,
      height: null,
    };

    setSalesOrderData((prev) => ({
      ...prev,
      inventories: [...(prev.inventories || []), newRow],
    }));
    setChargeModalOpen(false);
  };

  const handleDeleteChargeRow = (recordId) => {
    setSalesOrderData((prev) => ({
      ...prev,
      inventories: (prev.inventories || []).filter(
        (ele) => ele._id !== recordId,
      ),
    }));
  };

  const baseColumns = [
    {
      title: "Vendor",
      dataIndex: "CodeNo",
      key: "CodeNo",
      align: "center",
      render: (_, record) => {
        if (record?.lineType) {
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <Tooltip
                title={
                  record?.ownedByGst
                    ? `GST: ${record.ownedByGst}`
                    : "No GST on file"
                }
              >
                <span>{record?.ownedBy || "-"}</span>
              </Tooltip>
              <Tooltip title="Remove">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteChargeRow(record._id)}
                />
              </Tooltip>
            </div>
          );
        }
        if (!record?.isTrading) return record?.CodeNo;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <span>{record?.ownedBy || "-"}</span>
            <Tooltip title="Update Owned By">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openOwnedByModal(record)}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Suffix",
      dataIndex: "suffix",
      key: "suffix",
      align: "center",
    },
    {
      title: "Location",
      dataIndex: "locationName",
      key: "locationName",
      align: "center",
      width: 300,
      render: (text, record) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            maxWidth: "300px",
          }}
        >
          {record?.lineType
            ? CHARGE_TYPE_LABELS[record.lineType] || record.lineType
            : text}
        </div>
      ),
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      align: "center",
      render: (_, record) => {
        if (record?.lineType) return null;
        return `${record?.width} x ${record?.height}`;
      },
    },
  ];

  const printingMountingColumns = [
    {
      title: "Printing Charge",
      dataIndex: "printingCharge",
      key: "printingCharge",
      align: "center",
      render: (_, record) => {
        const logInventory = getPreviousInventory(record._id);
        const previousPrintingCharge = logInventory?.printingCharge;
        const hasChanged = logInventory
          ? hasValueChanged(record?.printingCharge, previousPrintingCharge)
          : false;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                padding: "2px 6px",
                borderRadius: 4,
                backgroundColor: hasChanged ? "#fff0f0" : "transparent",
              }}
            >
              {record?.printingCharge ?? 0}
            </span>
            {hasChanged && (
              <Tooltip
                trigger={["hover", "click"]}
                title={`Previous Printing Charge: ${previousPrintingCharge || 0}`}
              >
                <InfoCircleOutlined
                  style={{
                    color: hasChanged ? "#ff4d4f" : "#1677ff",
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Mounting Charge",
      dataIndex: "mountingCharge",
      key: "mountingCharge",
      align: "center",
      render: (_, record) => {
        const logInventory = getPreviousInventory(record._id);
        const previousMountingCharge = logInventory?.mountingCharge;
        const hasChanged = logInventory
          ? hasValueChanged(record?.mountingCharge, previousMountingCharge)
          : false;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                padding: "2px 6px",
                borderRadius: 4,
                backgroundColor: hasChanged ? "#fff0f0" : "transparent",
              }}
            >
              {record?.mountingCharge ?? 0}
            </span>
            {hasChanged && (
              <Tooltip
                trigger={["hover", "click"]}
                title={`Previous Mounting Charge: ${previousMountingCharge || 0}`}
              >
                <InfoCircleOutlined
                  style={{
                    color: hasChanged ? "#ff4d4f" : "#1677ff",
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const subBdfColumns = [...baseColumns, ...printingMountingColumns];

  const fullColumns = [
    ...baseColumns,
    ...(!hideprintingMounting ? printingMountingColumns : []),
    !hidePurchaseRate && {
      title: "Purchase Rate",
      dataIndex: "purchaseRate",
      key: "purchaseRate",
      align: "center",
      render: (_, record) => {
        if (salesOrderData?.bdfType === "F") {
          return "-";
        }

        // console.log("record is: ", record);

        const purchaseRate = record.maintenanceRate;
        const isDisabled = !ableToModifyPurchaseRate(userId);
        const logInventory = getPreviousInventory(record._id);
        const hasChanged = logInventory
          ? hasValueChanged(purchaseRate, logInventory?.maintenanceRate)
          : false;

        return (
          <Input
            style={{
              width: "100px",
              backgroundColor: hasChanged ? "#fff0f0" : "transparent",
            }}
            value={purchaseRate}
            disabled={isDisabled}
            onChange={(e) => {
              handleInvChange(e.target.value, record._id, "maintenanceRate");
            }}
          />
        );
      },
    },

    {
      title: "Billing Amount",
      dataIndex: "specialRate",
      key: "specialRate",
      align: "center",
      render: (_, record) => {
        if (record?.lineType) return null;
        const logInventory = getPreviousInventory(record._id);
        const previousSpecialRate = logInventory?.specialRate;
        const hasChanged = logInventory
          ? hasValueChanged(record?.specialRate, previousSpecialRate)
          : false;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <Input
              style={{
                width: "100px",
                backgroundColor: hasChanged ? "#fff0f0" : "transparent",
              }}
              disabled={salesOrderData?.isFromSub}
              value={record?.specialRate}
              onChange={(e) =>
                handleInvChange(e.target.value, record._id, "specialRate")
              }
            />
            {hasChanged && (
              <Tooltip
                trigger={["hover", "click"]}
                title={`Previous Billing Amount: ${previousSpecialRate || 0}`}
              >
                <InfoCircleOutlined
                  style={{
                    color: hasChanged ? "#ff4d4f" : "#1677ff",
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <div style={{ whiteSpace: "pre-line", textAlign: "center" }}>
          Special Rate{"\n"}Per Month
        </div>
      ),
      dataIndex: "specialRatePerMonth",
      key: "specialRatePerMonth",
      align: "center",
      render: (_, record) => {
        if (record?.lineType) return null;
        const logInventory = getPreviousInventory(record._id);
        const hasChanged = logInventory
          ? hasValueChanged(
              record?.specialRatePerMonth,
              logInventory?.specialRatePerMonth,
            )
          : false;

        return (
          <Input
            style={{
              width: "100px",
              backgroundColor: hasChanged ? "#fff0f0" : "transparent",
            }}
            value={record?.specialRatePerMonth}
            onChange={(e) =>
              handleInvChange(e.target.value, record._id, "specialRatePerMonth")
            }
          />
        );
      },
    },
    {
      title: "Booking Period",
      dataIndex: "Booking Period",
      key: "Booking Period",
      align: "center",
      render: (_, record) => {
        const rangeValue =
          record.startDate && record.endDate
            ? [
                dayjs(record.startDate, "YYYY-MM-DD"),
                dayjs(record.endDate, "YYYY-MM-DD"),
              ]
            : [];

        // Find the previous log entry for this inventory
        const logInventory = getPreviousInventory(record._id);

        // Check if dates have changed
        const hasChanged =
          logInventory &&
          (logInventory.startDate !== record.startDate ||
            logInventory.endDate !== record.endDate);

        return (
          <DatePicker.RangePicker
            value={rangeValue}
            disabled={true}
            format={"DD-MM-YYYY"}
            style={{
              width: "230px",
              backgroundColor: hasChanged ? "#fff0f0" : "transparent",
            }}
            // onChange={(dates, dateStrings) => {
            //   handleDateRangeChange(dates, dateStrings, record._id);
            // }}
          />
        );
      },
    },
    {
      title: (
        <div style={{ whiteSpace: "pre-line", textAlign: "center" }}>
          Duration{"\n"}(Days)
        </div>
      ),
      dataIndex: "Duration (Days)",
      key: "Duration (Days)",
      align: "center",
      render: (_, record) => {
        if (record.startDate && record.endDate) {
          const duration =
            dayjs(record.endDate).diff(dayjs(record.startDate), "day") + 1;
          return <span>{duration}</span>;
        }
        return <span>-</span>;
      },
    },
    {
      title: "Profit/Loss",
      dataIndex: "Profit/Loss",
      key: "Profit/Loss",
      align: "center",
      render: (_, record) => {
        if (record?.lineType) return null;
        const { specialRate, maintenanceRate } = record;

        const profitLoss =
          maintenanceRate != null && specialRate
            ? ((specialRate - maintenanceRate) / specialRate) * 100
            : 0;

        const color = profitLoss >= 0 ? "green" : "red";

        return <div style={{ color }}>{profitLoss.toFixed(2)}%</div>;
      },
    },
  ].filter(Boolean);

  const columns = isSubBdf ? subBdfColumns : fullColumns;

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  return (
    <div>
      <Modal
        title="Update Owned By of Trading Inventory"
        open={ownedByModalOpen}
        onCancel={() => setOwnedByModalOpen(false)}
        onOk={handleOwnedBySave}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={ownedBySaving}
        destroyOnClose
      >
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Owned By</div>
        <Select
          value={ownedByValue}
          onChange={setOwnedByValue}
          style={{ width: "100%" }}
          options={[{ label: "Other Trading", value: "Other Trading" }]}
        />
      </Modal>

      <Modal
        title="Add Agency Commission / Transportation"
        open={chargeModalOpen}
        onCancel={() => setChargeModalOpen(false)}
        onOk={handleAddCharge}
        okText="Add"
        cancelText="Cancel"
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Type</div>
          <Select
            value={chargeType}
            onChange={setChargeType}
            style={{ width: "100%" }}
            options={[
              { label: "Agency Commission", value: "AGENCY_COMMISSION" },
              { label: "Transportation", value: "TRANSPORTATION" },
            ]}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Amount</div>
          <InputNumber
            value={chargeAmount}
            onChange={setChargeAmount}
            min={0}
            style={{ width: "100%" }}
            placeholder="Enter amount"
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            Duration
          </div>
          <DatePicker.RangePicker
            value={chargeDates}
            onChange={(dates) => setChargeDates(dates || [])}
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            Party (search by name or GST number)
          </div>
          <Select
            showSearch
            value={chargePartyId}
            onChange={handlePartySelect}
            onSearch={searchParties}
            onFocus={() => searchParties("")}
            filterOption={false}
            loading={partySearching}
            notFoundContent={partySearching ? "Searching..." : "No parties found"}
            placeholder="Search party by name or GST number"
            style={{ width: "100%" }}
            options={partyOptions.map((party) => ({
              label: partyOptionLabel(party),
              value: party?._id,
            }))}
          />
        </div>
      </Modal>

      {!isSubBdf && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <Button icon={<PlusOutlined />} onClick={openChargeModal}>
            Add Agency Commission
          </Button>
          <span>Printing & Mounting</span>
          <Switch
            checked={hideprintingMounting}
            onChange={(checked) => setHidePrintingMounting(checked)}
            checkedChildren="Hidden"
            unCheckedChildren="Visible"
          />
          <span>Purchase Rate</span>
          <Switch
            checked={hidePurchaseRate}
            onChange={(checked) => setHidePurchaseRate(checked)}
            checkedChildren="Hidden"
            unCheckedChildren="Visible"
          />
        </div>
      )}

      <Table
        columns={columns}
        dataSource={dataSource}
        components={components}
        scroll={{ x: "max-content" }}
        pagination={false}
      />

      {/* Totals Summary Section - hidden for Sub BDF */}
      {!isSubBdf && (
        <Card
          className="totals-card"
          style={{
            marginTop: "20px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.09)",
            borderRadius: "8px",
          }}
        >
          <Divider
            orientation="left"
            style={{ fontWeight: "bold", fontSize: "16px" }}
          >
            SUMMARY TOTALS
          </Divider>
          <Row gutter={[24, 16]} justify="space-around">
            {!hidePurchaseRate && (
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Total Purchase Rate"
                  value={
                    salesOrderData?.bdfType === "F"
                      ? 0
                      : Math.round(totals?.purchaseRate)
                  }
                  style={{ textAlign: "center" }}
                />
              </Col>
            )}
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Total Billing Amount"
                value={Math.round(totals?.billingAmount)}
                style={{ textAlign: "center" }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="P/L Amount"
                value={
                  salesOrderData?.bdfType === "F"
                    ? Math.round(totals?.billingAmount)
                    : Math.round(totals?.billingAmount - totals?.purchaseRate)
                }
                style={{ textAlign: "center" }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Average Profit/Loss"
                value={Math.round(
                  totals?.billingAmount !== 0
                    ? ((totals?.billingAmount - totals?.purchaseRate) /
                        totals?.billingAmount) *
                        100
                    : 0,
                )}
                suffix="%"
                valueStyle={{
                  color:
                    (totals?.billingAmount - totals?.purchaseRate) /
                      totals?.billingAmount >=
                    0
                      ? "green"
                      : "red",
                }}
                style={{ textAlign: "center" }}
              />
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default InventoryList;
